/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useContext, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { useAppSelector } from '../../../reducers';
import { generateUrl } from '../../../routes/util';
import { getCurrentUser } from '../../../selectors/user';
import { SurveyType } from '@g17eco/types/surveyCommon';
import { Menu } from '@g17eco/molecules/menu/Menu';
import { SurveyContext } from '@features/survey-container/SurveyContainer';
import { SURVEY } from '@constants/terminology';
import { BasicAlert } from '@g17eco/molecules/alert';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { SurveyPermissions } from '@services/permissions/SurveyPermissions';

interface SurveySettingsMenuProps {
  isReadOnly?: boolean;
}

export const SurveySettingsMenu = (props: SurveySettingsMenuProps) => {
  const { isReadOnly } = props;

  const history = useHistory();
  const { surveyData } = useContext(SurveyContext);
  const isCombinedReport = useMemo(() => surveyData.type === SurveyType.Aggregation, [surveyData]);

  const isDisabled = Boolean(surveyData.completedDate);

  const isManager = useAppSelector((state) => {
    if (!state.survey.loaded) {
      return false;
    }
    const currentUser = getCurrentUser(state);
    return currentUser && SurveyPermissions.canManage(state.survey.data, currentUser);
  });

  const params = useParams<{ surveyId: string, initiativeId: string, page: string }>();
  const surveyId = surveyData._id ?? params.surveyId;
  const initiativeId = surveyData.initiativeId ?? params.initiativeId;
  const page = params.page;

  const isCreate = !surveyId;
  const isToolbarDisabled = Boolean(isCreate || !isManager || isReadOnly);

  const getItem = (props: { code: string, disabled?: boolean, label: string }) => {
    const { code, label, disabled } = props;

    const isActive = (code === page) || (isCreate && code === 'configuration');
    const onClick = isActive ? undefined : () => (
      history.push({
        pathname: generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId, page: code }),
        search: history.location.search,
      })
    );

    const isDisabled = (isToolbarDisabled || disabled);
    const upgradeMessage = 'This feature is not available on your account. Please contact your Account Manager for more information';

    const text = isDisabled ?
      <SimpleTooltip text={upgradeMessage}>{label}</SimpleTooltip>
      : label;

    return {
      label: text,
      disabled: isDisabled,
      active: isActive,
      onClick
    }
  }

  const items = [
    getItem({ code: 'configuration', label: 'Configuration' })
  ];

  if (!isCombinedReport) {
    items.push(getItem({ code: 'scope', label: `${SURVEY.CAPITALIZED_SINGULAR} scope` }), getItem({ code: 'delegation', label: 'Delegation' }));
  }

  return (
    <>
      {isDisabled ? (
        <BasicAlert type='warning'>
          All settings as locked because this {SURVEY.SINGULAR} is marked as Completed.
        </BasicAlert>
      ) : null}
      <Menu items={items} />
      <hr />
    </>
  );
};
